/**
 * V81.6: 오늘 승인 휴가 interval 기반 effective work end time 계산
 *
 * ⚠️ SSoT 동기화 필수:
 * 이 함수는 BE `functions/src/modules/attendance/functions.ts`의
 * `computeEffectiveWorkEndMinutes()` 와 **완전 동일한 알고리즘**을 유지해야 한다.
 * 둘 중 한 쪽만 수정되면 FE 퇴근 판정과 BE 저장 캡핑 기준이 어긋남(drift).
 *
 * 사용처:
 * - FE: WorkMyDashboardPage.tsx — `isBeforeEndTime` (퇴근 확인 팝업 판정)
 * - BE: checkOutAttendance — checkOutTime 저장 캡핑
 *
 * 원리:
 * baseEnd에서 시작해 effectiveEnd를 역방향 수렴.
 *   - iv.end >= effectiveEnd && iv.start < effectiveEnd → effectiveEnd = iv.start
 *   - 더 이상 수렴 가능한 interval이 없을 때까지 반복
 *
 * 지원 타입:
 *   - 오후반차 / 퍼스널힐링데이 → [baseEnd-4h, baseEnd] interval
 *   - 오전반차                  → [baseStart, baseEnd-4h] interval
 *   - 시간단위 (hourly)         → [startTime, endTime] interval
 *   - 전일 (fullDay)            → [baseStart, baseEnd] interval
 *
 * 중간 구간 hourly(예: 14:00~15:00)는 effectiveEnd에 연속되지 않으므로 영향 없음.
 */

export interface LeaveIntervalInput {
  leaveTypeCode?: string;
  unit?: 'fullDay' | 'morningHalf' | 'afternoonHalf' | 'hourly' | string;
  /** hourly 전용: 'HH:mm' */
  startTime?: string;
  /** hourly 전용: 'HH:mm' */
  endTime?: string;
}

/**
 * @param baseStartTime 'HH:mm' — workSchedule.base.startTime (override 우선)
 * @param baseEndTime   'HH:mm' — workSchedule.base.endTime (override 우선)
 * @param leaves        오늘 승인된 휴가 목록 (하루치, 이미 날짜 필터링 완료)
 * @returns effective work end time in minutes (0~1440)
 */
export function computeEffectiveWorkEndMinutes(
  baseStartTime: string,
  baseEndTime: string,
  leaves: LeaveIntervalInput[],
): number {
  const toMin = (hhmm: string): number => {
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
  };
  const baseStartMin = toMin(baseStartTime);
  const baseEndMin = toMin(baseEndTime);
  const HALF_DAY_OFFSET = 240; // 4h
  const halfBoundaryMin = baseEndMin - HALF_DAY_OFFSET;

  // 1) 모든 leave를 근무시간 기준 interval [startMin, endMin) 로 변환
  const intervals: Array<{ start: number; end: number }> = [];
  for (const leave of leaves) {
    const { leaveTypeCode, unit, startTime, endTime } = leave;

    // 전일 휴가 — 근무시간 전체 점유
    if (unit === 'fullDay') {
      intervals.push({ start: baseStartMin, end: baseEndMin });
      continue;
    }
    // 오후반차 / 퍼스널힐링데이 — [baseEnd-4h, baseEnd]
    if (
      unit === 'afternoonHalf' ||
      leaveTypeCode === 'halfDayAfternoon' ||
      leaveTypeCode === 'personalHealing'
    ) {
      intervals.push({ start: halfBoundaryMin, end: baseEndMin });
      continue;
    }
    // 오전반차 — [baseStart, baseEnd-4h] (종료 캡핑에는 영향 없지만 완전성)
    if (unit === 'morningHalf' || leaveTypeCode === 'halfDayMorning') {
      intervals.push({ start: baseStartMin, end: halfBoundaryMin });
      continue;
    }
    // 시간단위 (대체휴무 hourly 등)
    if (unit === 'hourly' && startTime && endTime) {
      const s = toMin(startTime);
      const e = toMin(endTime);
      if (e > s) intervals.push({ start: s, end: e });
      continue;
    }
  }

  // 2) effectiveEnd 역방향 수렴
  let effectiveEnd = baseEndMin;
  let changed = true;
  while (changed) {
    changed = false;
    for (let i = 0; i < intervals.length; i++) {
      const iv = intervals[i];
      if (iv.end >= effectiveEnd && iv.start < effectiveEnd) {
        effectiveEnd = iv.start;
        intervals.splice(i, 1);
        changed = true;
        break;
      }
    }
  }

  return Math.max(baseStartMin, effectiveEnd);
}
